import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { translatedZodResolver } from '@/lib/utils/validation/translated-zod-resolver';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateLunchMenu } from '../../hooks/use-manage-lunch';
import { menuFormSchema, MenuFormValues } from '../../schemas/menu-form-schema';
import { WEEKDAYS } from '../../types/manage-lunch.types';

export interface MenuFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MenuFormDialog = ({ open, onOpenChange }: MenuFormDialogProps) => {
  const { t } = useTranslation();
  const { mutateAsync: createLunchMenu, isPending } = useCreateLunchMenu();
  const [menuItemsText, setMenuItemsText] = useState('');

  const form = useForm<MenuFormValues>({
    resolver: translatedZodResolver(menuFormSchema, t),
    defaultValues: { Name: '', WeekDay: WEEKDAYS[0], MenuItems: [] },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ Name: '', WeekDay: WEEKDAYS[0], MenuItems: [] });
    setMenuItemsText('');
  }, [open, form]);

  const handleSubmit = async (values: MenuFormValues) => {
    try {
      await createLunchMenu({ ...values, IsActive: true });
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('ADD_MENU')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="WeekDay"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('WEEKDAY')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('WEEKDAY')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {WEEKDAYS.map((day) => (
                        <SelectItem key={day} value={day}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('MENU_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('MENU_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="MenuItems"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('MENU_ITEMS')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('MENU_ITEMS_PLACEHOLDER')}
                      value={menuItemsText}
                      onBlur={field.onBlur}
                      onChange={(e) => {
                        setMenuItemsText(e.target.value);
                        field.onChange(
                          e.target.value
                            .split(',')
                            .map((item) => item.trim())
                            .filter(Boolean)
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isPending} disabled={isPending}>
                {t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
